import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { GlobalRulesOverviewOverallSchema } from '@brighterion/types/overview';
import {
    RQ_GLOBAL_RULES,
    RQ_HISTORY,
    RQ_LATEST_REPOSITORY_CONFIG,
    RQ_PARAMETERS,
    RQ_RULE_GROUPS,
    RQ_TENANTS
} from '@brighterion/utils/react-query-constants';
import { ReactQueryError } from '@brighterion/types/react-query';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { updateOverallThreshold } from 'Services';
import { Levels } from 'Common/enums';

type UseUpdateOverallThreshold = {
    templateRepositoryId: number;
    level: Levels;
};

const useUpdateOverallThreshold = (props: UseUpdateOverallThreshold) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { addAlert } = useAlertBanner();
    const { addModal, addTechnicalErrorModal } = useReduxModal();

    return useMutation({
        mutationFn: async (updatedOverallThreshold: GlobalRulesOverviewOverallSchema) => {
            const data = await updateOverallThreshold(props.templateRepositoryId, props.level, updatedOverallThreshold);

            return data;
        },
        onSuccess: () => {
            addAlert({
                id: 'global-overview-run',
                status: 'success',
                content: t('globalOverview.successfullyUpdated')
            });
            queryClient.invalidateQueries([RQ_GLOBAL_RULES, props.templateRepositoryId]);
            queryClient.invalidateQueries([RQ_RULE_GROUPS]);
            queryClient.invalidateQueries([RQ_HISTORY]);
            queryClient.invalidateQueries([RQ_LATEST_REPOSITORY_CONFIG]);
            // if a tenant overview information changes in impersonation mode
            // we want to show the change in the tenant table in normal mode
            queryClient.invalidateQueries([RQ_TENANTS]);
            queryClient.invalidateQueries([RQ_PARAMETERS]);
        },
        onError: (error: ReactQueryError) => {
            if (error.status === 409) {
                addModal({
                    id: 'global-overview-warning',
                    status: 'warning',
                    title: t('errorHandling.globalRules.overviewTitle'),
                    content: t('errorHandling.globalRules.overviewPreamble'),
                    cancelText: t('Close')
                });
            } else {
                addTechnicalErrorModal(
                    t('errorHandling.globalRules.genericMessage'),
                    t('errorHandling.globalRules.overviewTitle')
                );
            }
        }
    });
};

export default useUpdateOverallThreshold;
